<template>
    <div class="bg-white overflow-hidden shadow rounded-lg divide-y divide-gray-200">
        <div class="-ml-4 -mt-2 flex items-center justify-between flex-wrap sm:flex-nowrap px-4 py-5 sm:p-6">
            <div class="ml-4 mt-2">
                <h3 class="text-lg leading-6 font-medium text-gray-900">Valuation</h3>
            </div>
        </div>
    
        <div class="px-4 py-5 sm:p-6">

            <Fieldset heading="CAP Valuations" subheading="Live CAP values for the asset">
                <div class="grid grid-cols-1 gap-x-8 sm:grid-cols-2">
                    <div class="col-span-1 sm:col-span-2">
                        <FormField :field="state.valuation_form.fields.cap_clean" namespace="valuation" :name="'cap_clean'" class="mb-4" :disabled="!state.valuation_form.fields.cap_clean.editable || state.valuation_form.fields.cap_clean.disabled" />
                    </div>
                    <div class="col-span-1 sm:col-span-2">
                        <FormField :field="state.valuation_form.fields.cap_average" namespace="valuation" :name="'cap_average'" class="mb-4" :disabled="!state.valuation_form.fields.cap_average.editable || state.valuation_form.fields.cap_average.disabled" />
                    </div>
                    <div class="col-span-1 sm:col-span-2">
                        <FormField :field="state.valuation_form.fields.cap_poor" namespace="valuation" :name="'cap_poor'" class="mb-4" :disabled="!state.valuation_form.fields.cap_poor.editable || state.valuation_form.fields.cap_poor.disabled" />
                    </div>

                </div>
            </Fieldset>

            <Fieldset heading="Tax Settings">
                <div class="grid grid-cols-1 gap-x-8 sm:grid-cols-2">
                    <div class="col-span-1 sm:col-span-2">
                        <div class="grid grid-cols-1 gap-x-8 sm:grid-cols-2">
                            <div class="col-span-1 sm:col-span-1">
                                <FormField :field="state.reserve_form.fields.vehicle_class" namespace="valuation" :name="'vehicle_class'" class="mb-4" />
                            </div>
                        </div>
                    </div>

                    <div class="col-span-1 sm:col-span-2">
                        <div class="grid grid-cols-1 gap-x-8 sm:grid-cols-2">
                            <div class="col-span-1 sm:col-span-1">
                                <FormField :field="state.proxy_tax.fields.tax_status" namespace="proxy_tax" :name="'tax_status'" class="mb-0" />
                            </div>
                        </div>
                    </div>
                </div>
            </Fieldset>

            <Fieldset heading="Reserve & SIV" subheading="Enter the NET values without tax and we'll calculate any tax for you">
                <div class="grid grid-cols-1 gap-x-8 sm:grid-cols-2">
                    <div class="col-span-1 sm:col-span-1">
                        <FormField :field="state.valuation_form.fields.stand_in_value" namespace="valuation" :name="'stand_in_value'" class="mb-4" />
                    </div>
                    <div class="col-span-1 sm:col-span-1">
                        <FormDisplayData :active="true" label="Gross Stand in Value (inc. VAT)" :value="taxable_stand_in_value || 'No VAT'" />
                    </div>

                    <div class="col-span-1 sm:col-span-2">
                        <div class="grid grid-cols-1 gap-x-8 sm:grid-cols-2">
                            <div class="col-span-1 sm:col-span-1">
                                <FormField :field="state.reserve_form.fields.master_reserve_status" namespace="reserve" :name="'master_reserve_status'" class="mb-4" />
                            </div>
                        </div>
                    </div>

                    <div v-if="state.reserve_form.fields.master_reserve.active" class="col-span-1 sm:col-span-1">
                        <FormField :field="state.reserve_form.fields.master_reserve" namespace="reserve" :name="'master_reserve'" class="mb-4" />
                        <Message
                            :active="state.reserve_warning.active"
                            type="info"
                            :heading="state.reserve_warning.heading"
                            :body="state.reserve_warning.body"
                            class="mb-2"
                        />
                    </div>

                    <div v-if="state.reserve_form.fields.master_reserve.active" class="col-span-1 sm:col-span-1">
                        <FormDisplayData :active="true" label="Gross Reserve (inc. VAT)" :value="taxable_master_reserve || 'No VAT'" />
                    </div>
                </div>
            </Fieldset>

        </div>
    
        <div class="px-4 py-4 sm:px-6 text-right">
            <Button type="button" width="w-auto" color="blue-outline" :onClick="VEHICLE.activatePrevStep">Back</Button>
            <Button type="button" width="w-auto" class="ml-2" :onClick="() => submitStep()" :disabled="!next_button_active">Next</Button>
        </div>
    </div>
</template>

<script>
import { useVehicleStore } from '@sto/vehicle.store'
import { useSystemStore } from '@sto/system.store'
import * as VehicleService from '@ser/vehicle'
import { useAssetStore } from "@sto/asset.store"
import { computed, onMounted, onBeforeMount, reactive, watch } from 'vue'
import LoadingIcon from '@ico/LoadingIcon.vue'
import { isValid, getPayload, poundsToPence, penceToPounds, monetise } from '@lib/helpers'
import { useRoute } from 'vue-router'
import config from '@/config'
import Fieldset from "@glo/Fieldset.vue";
export default {
    components: {
        Fieldset,
        LoadingIcon
    },
    setup() {
        const VEHICLE = useVehicleStore()
        const ASSET_STORE = useAssetStore()
        const ROUTE = useRoute()
        const SYSTEM_STORE = useSystemStore()

        const vehicle_id = computed(() => {
            return VEHICLE?.vehicle?.id || null
        })

        const vehicle_data = computed(() => {
            return VEHICLE?.vehicle || null
        })

        const vehicle_asset_id = computed(() => {
            return VEHICLE?.vehicle?.asset?.id || null
        })

        const next_button_active = computed(() => {

            let active = true

            for (let f in state.valuation_form.fields) {
                if (state.valuation_form.fields[f].required && !state.valuation_form.fields[f].value) active = false
            }

            return active
        })

        const initialProxyTaxStatus = tax_status => {
            if (typeof tax_status === 'string' && tax_status.includes('apply_vat')) return 'apply_vat'
            if (typeof tax_status === 'string' && tax_status.includes('vat_qualifying')) return 'vat_qualifying'
            return 'no_vat'
        }

        const default_vehicle_class = vehicle_data?.value?.vehicle_class || 'CAR'
        const default_tax_status = vehicle_data?.value?.asset?.tax_status || (default_vehicle_class === 'LCV' ? 'commercial_no_vat' : 'car_no_vat')

        const state = reactive({
            valuation_form: {
                valid: false,
                has_changes: computed(() => {
                    let has_changes = false
                    for (let f in state.valuation_form.fields) {
                        if (state.valuation_form.fields[f].value != state.valuation_form.fields[f].stored_value) has_changes = true
                    }
                    return has_changes
                }),
                loading: false,
                fields: {
                    cap_clean: {
                        active: true,
                        label: 'CAP clean',
                        type: 'number',
                        value: vehicle_data?.value?.cap_clean ? penceToPounds(vehicle_data?.value?.cap_clean) : '',
                        stored_value: vehicle_data?.value?.cap_clean ? penceToPounds(vehicle_data?.value?.cap_clean) : '',
                        required: false,
                        pristine: true,
                        valid: false,
                        transformer: poundsToPence,
                        message: '',
                        validation: [],
                        notes: '',
                        editable: false,
                        disabled: false
                    },
                    cap_average: {
                        active: true,
                        label: 'CAP average',
                        type: 'number',
                        value: vehicle_data?.value?.cap_average ? penceToPounds(vehicle_data?.value?.cap_average) : '',
                        stored_value: vehicle_data?.value?.cap_average ? penceToPounds(vehicle_data?.value?.cap_average) : '',
                        required: false,
                        pristine: true,
                        valid: false,
                        transformer: poundsToPence,
                        message: '',
                        validation: [],
                        notes: '',
                        editable: false,
                        disabled: false
                    },
                    cap_poor: {
                        active: true,
                        label: 'CAP poor',
                        type: 'number',
                        value: vehicle_data?.value?.cap_poor ? penceToPounds(vehicle_data?.value?.cap_poor) : '',
                        stored_value: vehicle_data?.value?.cap_poor ? penceToPounds(vehicle_data?.value?.cap_poor) : '',
                        required: false,
                        pristine: true,
                        valid: false,
                        transformer: poundsToPence,
                        message: '',
                        validation: [],
                        notes: '',
                        editable: false,
                        disabled: false
                    },
                    stand_in_value: {
                        active: true,
                        label: 'Stand in value (exc. VAT)',
                        type: 'number',
                        value: vehicle_data?.value?.stand_in_value ? penceToPounds(vehicle_data?.value?.stand_in_value) : '',
                        stored_value: vehicle_data?.value?.stand_in_value ? penceToPounds(vehicle_data?.value?.stand_in_value) : '',
                        required: false,
                        pristine: true,
                        valid: false,
                        transformer: poundsToPence,
                        message: '',
                        validation: [],
                        notes: '',
                        editable: true,
                        disabled: false
                    }
                }
            },
            reserve_form: {
                valid: false,
                has_changes: computed(() => {
                    let has_changes = false
                    for (let f in state.reserve_form.fields) {
                        if (state.reserve_form.fields[f].value != state.reserve_form.fields[f].stored_value) has_changes = true
                    }
                    return has_changes
                }),
                loading: false,
                fields: {
                    master_reserve_status: {
                        active: true,
                        label: 'Set a reserve?',
                        type: 'select',
                        value: vehicle_data?.value?.asset?.master_reserve_status || 'tbc',
                        stored_value: vehicle_data?.value?.asset?.master_reserve_status || '',
                        options: [
                            {
                                value: 'no_reserve',
                                label: 'No reserve'
                            },
                            {
                                value: 'tbc',
                                label: 'To be confirmed'
                            },
                            {
                                value: 'use_reserve',
                                label: 'Set reserve'
                            }
                        ],
                        required: true,
                        pristine: vehicle_data?.value?.asset?.master_reserve_status ? true : false,
                        valid: false,
                        message: '',
                        validation: [],
                        notes: '',
                        editable: false,
                        disabled: false
                    },
                    master_reserve: {
                        active: vehicle_data?.value?.asset?.master_reserve_status == 'use_reserve',
                        label: 'Reserve (exc. VAT)',
                        type: 'number',
                        value: vehicle_data?.value?.asset?.master_reserve ? penceToPounds(vehicle_data?.value?.asset?.master_reserve) : '',
                        stored_value: vehicle_data?.value?.asset?.master_reserve ? penceToPounds(vehicle_data?.value?.asset?.master_reserve) : '',
                        required: false,
                        pristine: true,
                        valid: false,
                        transformer: poundsToPence,
                        message: '',
                        validation: [],
                        notes: '',
                        editable: true,
                        disabled: false
                    },
                    master_opening_bid: {
                        active: true,
                        value: vehicle_data?.value?.asset?.master_opening_bid ? penceToPounds(vehicle_data?.value?.asset?.master_opening_bid) : null,
                        stored_value: vehicle_data?.value?.asset?.master_opening_bid ? penceToPounds(vehicle_data?.value?.asset?.master_opening_bid) : null,
                        label: 'Master Opening Bid',
                        type: 'hidden',
                        required: false,
                        pristine: true,
                        valid: true,
                        transformer: poundsToPence,
                        validation: [],
                        message: '',
                        notes: '',
                        disabled: false
                    },
                    bid_interval: {
                        active: true,
                        value: vehicle_data?.value?.asset?.bid_interval ? penceToPounds(vehicle_data?.value?.asset?.bid_interval) : null,
                        stored_value: vehicle_data?.value?.asset?.bid_interval ? penceToPounds(vehicle_data?.value?.asset?.bid_interval) : null,
                        label: 'Master Bid interval',
                        type: 'hidden',
                        default_value: false,
                        required: false,
                        pristine: true,
                        valid: true,
                        transformer: poundsToPence,
                        validation: [],
                        message: '',
                        notes: '',
                        disabled: false
                    },
                    vehicle_class: {
                        active: true,
                        label: 'Vehicle class',
                        type: 'radio',
                        value: default_vehicle_class,
                        stored_value: '',
                        options: [
                            {
                                value: 'CAR',
                                label: 'Passenger'
                            },
                            {
                                value: 'LCV',
                                label: 'Commercial'
                            }
                        ],
                        layout: 'inline',
                        required: true,
                        pristine: true,
                        valid: false,
                        message: '',
                        validation: [],
                        notes: '',
                        editable: true,
                        disabled: false
                    },
                    tax_status: {
                        active: true,
                        type: 'hidden',
                        value: default_tax_status,
                        stored_value: '',
                        required: false,
                        pristine: true,
                        valid: false,
                        message: '',
                        validation: [],
                        notes: '',
                        editable: false,
                        disabled: false
                    },
                }
            },
            proxy_tax: {
                fields: {
                    tax_status: {
                        active: true,
                        label: 'Tax status',
                        type: 'radio',
                        value: initialProxyTaxStatus(vehicle_data?.value?.asset?.tax_status),
                        stored_value: '',
                        options: [
                            {
                                value: 'no_vat',
                                label: 'No VAT'
                            },
                            {
                                value: 'apply_vat',
                                label: 'Apply VAT'
                            },
                            {
                                value: 'vat_qualifying',
                                label: 'VAT Qualifying'
                            }
                        ],
                        layout: 'inline',
                        required: true,
                        pristine: true,
                        valid: false,
                        message: '',
                        validation: [],
                        notes: '',
                        editable: true,
                        disabled: false
                    }
                }
            },
            reserve_warning: {
                active: false,
                heading: null,
                body: null
            }
        })

        const taxable_master_reserve = computed(() => {
            if (!state.reserve_form.fields.master_reserve.value || !state.reserve_form.fields.master_reserve.active || !['commercial_apply_vat','commercial_vat_qualifying', 'car_apply_vat','car_vat_qualifying'].includes(state.reserve_form.fields.tax_status.value)) return null
            return monetise((1 + config.invoice.tax_rate) * poundsToPence(state.reserve_form.fields.master_reserve.value))
        })

        const taxable_stand_in_value = computed(() => {
            if (!state.valuation_form.fields.stand_in_value.value || !state.valuation_form.fields.stand_in_value.active || !['commercial_apply_vat','commercial_vat_qualifying', 'car_apply_vat','car_vat_qualifying'].includes(state.reserve_form.fields.tax_status.value)) return null
            return monetise((1 + config.invoice.tax_rate) * poundsToPence(state.valuation_form.fields.stand_in_value.value))
        })

        const submitStep = async () => {
            // fields will automatically create their own validation states
            if (state.valuation_form.loading || !isValid(state.valuation_form.fields)) return

            // if the value is present and unchaned, just skip to the next step
            if (!state.valuation_form.has_changes && !state.reserve_form.has_changes) {
                VEHICLE.activateNextStep()
                return
            }

            state.valuation_form.loading = true

            try {

                let vehicle_payload = getPayload(state.valuation_form.fields)
                vehicle_payload.vehicle_class = state.reserve_form.fields.vehicle_class.value

                const asset_payload = {
                    tax_status: state.reserve_form.fields.tax_status.value,
                    master_reserve_status: state.reserve_form.fields.master_reserve_status.value,
                    master_reserve: state.reserve_form.fields.master_reserve.value && !isNaN(state.reserve_form.fields.master_reserve.value) ? poundsToPence(state.reserve_form.fields.master_reserve.value) : null,
                    master_opening_bid: state.reserve_form.fields.master_opening_bid.value
                        ? poundsToPence(state.reserve_form.fields.master_opening_bid.value)
                        : null,
                    bid_interval: state.reserve_form.fields.bid_interval.value
                        ? poundsToPence(state.reserve_form.fields.bid_interval.value)
                        : null
                }

                const vehicle_update = await VEHICLE.update(VEHICLE.vehicle.id, vehicle_payload)
                const asset_update = await VEHICLE.updateAsset(VEHICLE.vehicle.asset?.id, asset_payload)
                VEHICLE.activateNextStep()
            } catch (error) {
                console.error('submitStep', error)
                // TODO handle error
            }

            state.valuation_form.loading = false
        }

        watch(
            () => vehicle_data.value,
            (new_value, old_value) => {
                
                if (vehicle_data?.value?.cap_clean) state.valuation_form.fields.cap_clean.value = penceToPounds(vehicle_data?.value?.cap_clean)
                if (vehicle_data?.value?.cap_average) state.valuation_form.fields.cap_average.value = penceToPounds(vehicle_data?.value?.cap_average)
                if (vehicle_data?.value?.cap_poor) state.valuation_form.fields.cap_poor.value = penceToPounds(vehicle_data?.value?.cap_poor)
                if (vehicle_data?.value?.stand_in_value) state.valuation_form.fields.stand_in_value.value = penceToPounds(vehicle_data?.value?.stand_in_value)
                // reserve
                if (vehicle_data?.value?.asset?.master_reserve_status) state.reserve_form.fields.master_reserve_status.value = vehicle_data?.value?.asset?.master_reserve_status
                if (vehicle_data?.value?.asset?.master_reserve) state.reserve_form.fields.master_reserve.value = penceToPounds(vehicle_data?.value?.asset?.master_reserve)
            }
        )

        const setMasterOpeningBid = reserve => {
            state.reserve_form.fields.master_opening_bid.value = !isNaN(reserve) && reserve > 0
                ? reserve
                : 200
        }

        const setBidInterval = reserve => {
            state.reserve_form.fields.bid_interval.value = ASSET_STORE.getSuggestedBidInterval(reserve)
        }

        watch(
            () => state.reserve_form.fields.master_reserve_status.value,
            (new_value, old_value) => {
                if (new_value == 'use_reserve') {
                    state.reserve_form.fields.master_reserve.active = true
                } else {
                    state.reserve_form.fields.master_reserve.active = false
                    state.reserve_form.fields.master_reserve.value = null
                }
                
            }
        )

        watch(
            () => state.reserve_form.fields.master_reserve.value,
            (new_value) => {
                if (!isNaN(new_value)) {
                    setMasterOpeningBid(new_value)
                    setBidInterval(new_value)
                }
            }
        )

        const setReserveWarning = (heading, body) => {
            state.reserve_warning.active = true
            state.reserve_warning.heading = heading || null
            state.reserve_warning.body = body || null
        }

        const clearReserveWarning = () => {
            state.reserve_warning.active = false
            state.reserve_warning.heading = null
            state.reserve_warning.body = null
        }

        const checkReserve = () => {
            if (state.reserve_form.fields.master_reserve_status.value != 'use_reserve' || !vehicle_data?.value?.cap_average) return clearReserveWarning()
            if (state.reserve_form.fields.master_reserve.value > (1.5 * (vehicle_data?.value?.cap_average / 100))) {
                setReserveWarning('That reserve might be too high', `The CAP average valuation for this vehicle is £${(vehicle_data?.value?.cap_average / 100)}`)
            } else if (state.reserve_form.fields.master_reserve.value < (0.5 * (vehicle_data?.value?.cap_average / 100))) {
                setReserveWarning('That reserve might be too low', `The CAP average valuation for this vehicle is £${(vehicle_data?.value?.cap_average / 100)}`)
            } else {
                clearReserveWarning()
            }
        }

        watch(
            () => state.reserve_form.fields.master_reserve.value,
            (new_value, old_value) => {
                checkReserve()
            }
        )

        /******/

        watch(
            () => state.reserve_form.fields.vehicle_class.value,
            (new_value) => {

                switch (new_value) {
                    case 'LCV':
                        state.reserve_form.fields.tax_status.value = `commercial_${state.proxy_tax.fields.tax_status.value}`
                        break
                    case 'CAR':
                        state.reserve_form.fields.tax_status.value = `car_${state.proxy_tax.fields.tax_status.value}`
                        break
                }
            }
        )

        watch(
            () => state.proxy_tax.fields.tax_status.value,
            (new_value) => {

                let prefix = state.reserve_form.fields.vehicle_class.value === 'LCV'
                    ? 'commercial'
                    : 'car'

                state.reserve_form.fields.tax_status.value = `${prefix}_${new_value}`
            }
        )

        onMounted(() => {
            isValid(state.valuation_form.fields)
            SYSTEM_STORE.createInteraction('STEP_UPDATE', ROUTE.fullPath, 'Vehicle valuation step', null)
            checkReserve()
        })

        return {
            VEHICLE,
            vehicle_data,
            state,
            submitStep,
            next_button_active,
            taxable_master_reserve,
            taxable_stand_in_value
        }
    }
}

</script>